import React from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Telegram } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useTelegramLink } from '@/hooks/useTelegramLink';

interface TelegramLinkButtonProps {
  chatId: number;
  messageId: number;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
}

export const TelegramLinkButton: React.FC<TelegramLinkButtonProps> = ({
  chatId,
  messageId,
  size = 'small',
  disabled = false,
}) => {
  const { t } = useTranslation('pages');
  const { data: linkData, isLoading, error } = useTelegramLink(chatId, messageId);

  const handleClick = () => {
    if (linkData?.link) {
      // Open in new tab
      window.open(linkData.link, '_blank', 'noopener,noreferrer');
    }
  };

  // Show loading spinner while fetching link
  if (isLoading) {
    return (
      <IconButton size={size} disabled>
        <CircularProgress size={size === 'small' ? 16 : 24} />
      </IconButton>
    );
  }

  if (error || !linkData) {
    return null;
  }

  // A chat with no Telegram id has no link to build. The control stays, greyed,
  // saying so — silently vanishing looks like a rendering bug.
  if (!linkData.accessible || !linkData.link) {
    return (
      <Tooltip title={t('chatPreview.messageActions.noLink')} arrow>
        <span>
          <IconButton size={size} disabled>
            <Telegram />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        linkData.is_public
          ? t('chatPreview.messageActions.openInTelegram')
          : t('chatPreview.messageActions.openInTelegramPrivate')
      }
      arrow
    >
      <IconButton
        onClick={handleClick}
        size={size}
        disabled={disabled}
        color="primary"
        sx={{
          '&:hover': {
            backgroundColor: 'primary.light',
          }
        }}
      >
        <Telegram />
      </IconButton>
    </Tooltip>
  );
};
